/**
 * Influencer Tracker — IdeaWorks
 * Track influencers across YouTube, TikTok, Meta (Facebook/Instagram), and more.
 * Check latest content, paste transcripts, AI-generate repurposed content.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  UserCheck, Plus, Trash2, Zap, RefreshCw, Copy, Check,
  TrendingUp, Repeat2, Brain, PlayCircle, ExternalLink, AlertCircle,
  Clock, Rss, ChevronDown, ChevronUp, Target, Sparkles, Link2, Globe,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = ['YouTube', 'TikTok', 'Meta (Facebook)', 'Instagram', 'Twitter/X', 'LinkedIn', 'Podcast'];
const TARGET_PLATFORMS = ['LinkedIn', 'Twitter/X', 'Instagram', 'TikTok', 'Reels (Meta)', 'Blogger', 'YouTube', 'qlia.io/blogs', 'iconiqs.ai/blogs'];
const CONTENT_TYPES = ['blog', 'social', 'video_script', 'newsletter'];
const REPURPOSE_PLATFORMS = [
  { id: 'linkedin', name: 'LinkedIn', color: '#0A66C2' },
  { id: 'twitter_x', name: 'Twitter/X', color: '#1DA1F2' },
  { id: 'instagram', name: 'Instagram', color: '#E1306C' },
  { id: 'tiktok', name: 'TikTok', color: '#69C9D0' },
  { id: 'reels_meta', name: 'Reels (Meta)', color: '#8B5CF6' },
  { id: 'youtube', name: 'YouTube', color: '#FF0000' },
  { id: 'facebook', name: 'Facebook', color: '#1877F2' },
];

const PLATFORM_ICONS = {
  'YouTube': { color: '#ff0000', label: 'YT' },
  'TikTok': { color: '#fe2c55', label: 'TT' },
  'Meta (Facebook)': { color: '#1877f2', label: 'FB' },
  'Instagram': { color: '#e4405f', label: 'IG' },
  'Twitter/X': { color: '#1da1f2', label: 'X' },
  'LinkedIn': { color: '#0a66c2', label: 'LI' },
  'Podcast': { color: '#a68eff', label: 'POD' },
};

export default function InfluencerTracker() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('influencer'); // 'influencer' | 'repurpose' | 'linkedin'
  const [liNiche, setLiNiche] = useState('AI & Technology');
  const [liTrends, setLiTrends] = useState([]);
  const [liLoading, setLiLoading] = useState(false);
  const [liRepurposing, setLiRepurposing] = useState(null);
  const [liRepurposed, setLiRepurposed] = useState({});
  const [liCopied, setLiCopied] = useState(null);
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', platform: 'YouTube', channel_url: '', channel_id: '', niche: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const [selectedInf, setSelectedInf] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [trendTopic, setTrendTopic] = useState('');
  const [targetPlatform, setTargetPlatform] = useState('LinkedIn');
  const [contentType, setContentType] = useState('blog');
  const [mode, setMode] = useState('ai_decide');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Latest content check
  const [checkingChannel, setCheckingChannel] = useState(false);
  const [channelData, setChannelData] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);

  // Repurpose tab state
  const [rpContent, setRpContent] = useState('');
  const [rpTitle, setRpTitle] = useState('');
  const [rpType, setRpType] = useState('blog');
  const [rpPlatforms, setRpPlatforms] = useState(REPURPOSE_PLATFORMS.map(p => p.id));
  const [rpLoading, setRpLoading] = useState(false);
  const [rpResults, setRpResults] = useState(null);
  const [rpExpanded, setRpExpanded] = useState({});

  useEffect(() => { loadInfluencers(); }, []);

  const loadInfluencers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/influencer`);
      setInfluencers(data.influencers || []);
    } catch {}
    setLoading(false);
  };

  const addInfluencer = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/influencer`, form);
      setInfluencers(prev => [data.influencer, ...prev]);
      setForm({ name: '', platform: 'YouTube', channel_url: '', channel_id: '', niche: '', notes: '' });
      setShowForm(false);
    } catch {}
    setSaving(false);
  };

  const deleteInfluencer = async (id) => {
    if (!window.confirm('Remove influencer?')) return;
    try {
      await axios.delete(`${API}/api/influencer/${id}`);
      setInfluencers(prev => prev.filter(i => i.id !== id));
      if (selectedInf?.id === id) { setSelectedInf(null); setChannelData(null); }
    } catch {}
  };

  const checkLatest = async (inf) => {
    setCheckingChannel(true);
    setChannelData(null);
    try {
      const { data } = await axios.post(`${API}/api/influencer/check-channel`, {
        influencer_id: inf.id,
        channel_url: inf.channel_url,
        channel_id: inf.channel_id,
        platform: inf.platform,
      });
      setChannelData(data);
      // Update the influencer in list with latest_videos
      if (data.latest_videos?.length > 0) {
        setInfluencers(prev => prev.map(i => i.id === inf.id ? { ...i, latest_videos: data.latest_videos } : i));
      }
    } catch (e) {
      setChannelData({ success: false, message: e?.response?.data?.detail || 'Failed to check channel' });
    } finally {
      setCheckingChannel(false);
    }
  };

  const loadVideoForTranscript = (video) => {
    setTranscript(`[From YouTube: ${video.title}]\n\nVideo URL: ${video.url}\n\nDescription:\n${video.description || '(No description — paste transcript here)'}`);
    setMode('repurpose');
  };

  const generate = async () => {
    if (mode !== 'trend' && !transcript.trim()) return;
    if (mode === 'trend' && !trendTopic.trim()) return;
    setGenerating(true);
    setResult(null);
    try {
      let endpoint, payload;
      if (mode === 'trend') {
        endpoint = '/api/influencer/trend-content';
        payload = { topic: trendTopic, target_platform: targetPlatform, content_type: contentType };
      } else if (mode === 'repurpose') {
        endpoint = '/api/influencer/repurpose';
        payload = { influencer_id: selectedInf?.id || '', transcript, target_platform: targetPlatform, content_type: contentType };
      } else {
        endpoint = '/api/influencer/ai-decide';
        payload = { influencer_id: selectedInf?.id || '', transcript, target_platform: targetPlatform, content_type: contentType };
      }
      const { data } = await axios.post(`${API}${endpoint}`, payload);
      setResult(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const copyContent = () => {
    if (!result?.content) return;
    const text = `${result.content.title}\n\n${result.content.body}\n\n${result.content.caption}\n\n${(result.content.hashtags || []).join(' ')}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const repurposeContent = async () => {
    if (rpContent.trim().length < 50 || rpPlatforms.length === 0) return;
    setRpLoading(true);
    setRpResults(null);
    try {
      const { data } = await axios.post(`${API}/api/repurpose/transform`, {
        content: rpContent, title: rpTitle, content_type: rpType,
        platforms: rpPlatforms, brand_id: 'default',
      });
      setRpResults(data.platforms);
      const exp = {};
      rpPlatforms.forEach(p => { exp[p] = true; });
      setRpExpanded(exp);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Repurpose failed');
    }
    setRpLoading(false);
  };

  const platMeta = (p) => PLATFORM_ICONS[p] || { color: '#aaa', label: p?.slice(0, 2).toUpperCase() };

  const fetchLinkedInTrends = useCallback(async () => {
    setLiLoading(true);
    setLiTrends([]);
    try {
      const { data } = await axios.post(`${API}/api/trends/linkedin-trends`, { niche: liNiche });
      setLiTrends(data.trending_posts || []);
    } catch {
      alert('Failed to fetch LinkedIn trends');
    }
    setLiLoading(false);
  }, [liNiche]);

  const repurposeLinkedIn = useCallback(async (post) => {
    setLiRepurposing(post.id);
    try {
      const { data } = await axios.post(`${API}/api/trends/linkedin-repurpose`, {
        hook: post.hook,
        body: post.body,
        format: post.format,
      });
      setLiRepurposed(prev => ({ ...prev, [post.id]: data.post }));
    } catch {
      alert('Repurpose failed');
    }
    setLiRepurposing(null);
  }, []);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Tab Navigation */}
      <div className="flex items-center gap-1 px-4 pt-3 pb-0 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
        {[
          { id: 'influencer', label: 'Influencer Hub', icon: UserCheck },
          { id: 'repurpose', label: 'Repurpose Content', icon: Repeat2 },
          { id: 'linkedin', label: 'LinkedIn Trends', icon: TrendingUp, badge: 'NEW' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setMainTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-all rounded-t-xl"
            style={{
              background: mainTab === tab.id ? 'rgba(249,115,22,0.1)' : 'transparent',
              borderBottom: mainTab === tab.id ? '2px solid #f97316' : '2px solid transparent',
              color: mainTab === tab.id ? '#f97316' : 'var(--llmc-text-muted)',
            }}>
            <tab.icon size={12} /> {tab.label}
            {tab.badge && <span className="text-[9px] font-black px-1 py-0.5 rounded ml-0.5" style={{ background: 'rgba(249,115,22,0.2)', color: '#f97316' }}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* ── Influencer Hub Tab ─────────────────────────────────────── */}
      {mainTab === 'influencer' && (
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Influencer list */}
          <div className="w-72 flex-shrink-0 flex flex-col overflow-hidden" style={{ borderRight: '1px solid var(--llmc-border)' }}>

          <div className="p-4 flex items-center justify-between flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center gap-2">
            <UserCheck size={16} style={{ color: '#f97316' }} />
            <span className="font-bold text-sm text-white">Influencers</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(249,115,22,0.12)', color: '#f97316' }}>{influencers.length}</span>
          </div>
          <button onClick={() => setShowForm(!showForm)} data-testid="add-influencer-btn"
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:opacity-80"
            style={{ background: 'rgba(249,115,22,0.15)', color: '#f97316' }}>
            <Plus size={14} />
          </button>
        </div>

        {showForm && (
          <div className="p-4 space-y-2 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="llmc-input w-full px-3 py-1.5 text-xs" placeholder="Channel / Creator Name" />
            <select value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}
              className="llmc-input w-full px-3 py-1.5 text-xs">
              {PLATFORMS.map(p => <option key={p}>{p}</option>)}
            </select>
            <input value={form.channel_url} onChange={e => setForm(f => ({ ...f, channel_url: e.target.value }))}
              className="llmc-input w-full px-3 py-1.5 text-xs" placeholder="Channel URL (e.g. youtube.com/channel/UCxxx)" />
            {form.platform === 'YouTube' && (
              <input value={form.channel_id} onChange={e => setForm(f => ({ ...f, channel_id: e.target.value }))}
                className="llmc-input w-full px-3 py-1.5 text-xs" placeholder="Channel ID (UCxxxxxx) — for auto-fetch" />
            )}
            <input value={form.niche} onChange={e => setForm(f => ({ ...f, niche: e.target.value }))}
              className="llmc-input w-full px-3 py-1.5 text-xs" placeholder="Niche (e.g. AI, Finance, Cybersecurity)" />
            <button onClick={addInfluencer} disabled={!form.name.trim() || saving}
              className="w-full py-1.5 rounded-lg text-xs font-bold disabled:opacity-40"
              style={{ background: 'rgba(249,115,22,0.2)', color: '#f97316' }}>
              {saving ? 'Saving...' : 'Add Influencer'}
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-xs text-center" style={{ color: 'var(--llmc-text-muted)' }}>Loading...</div>
          ) : influencers.length === 0 ? (
            <div className="p-6 text-xs text-center space-y-2" style={{ color: 'var(--llmc-text-muted)' }}>
              <UserCheck size={20} className="mx-auto opacity-30" />
              <div>No influencers tracked yet</div>
              <div className="opacity-60">Add YouTube, TikTok, or Meta creators to monitor</div>
            </div>
          ) : influencers.map(inf => {
            const pm = platMeta(inf.platform);
            const hasLatest = inf.latest_videos?.length > 0;
            return (
              <div key={inf.id}
                onClick={() => { setSelectedInf(inf); setChannelData(null); setResult(null); }}
                className={`p-3 cursor-pointer transition-all group ${selectedInf?.id === inf.id ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center text-[8px] font-black"
                      style={{ background: `${pm.color}20`, color: pm.color, border: `1px solid ${pm.color}30` }}>
                      {pm.label}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-white truncate">{inf.name}</div>
                      <div className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--llmc-text-muted)' }}>
                        {inf.niche || inf.platform}
                        {hasLatest && <span className="ml-1.5 text-green-400">● {inf.latest_videos.length} videos</span>}
                      </div>
                    </div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); deleteInfluencer(inf.id); }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded transition-all hover:text-red-400 flex-shrink-0"
                    style={{ color: 'var(--llmc-text-muted)' }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: Content Generation Panel */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Brain size={18} style={{ color: '#f97316' }} />
              <h1 className="text-lg font-black text-white">Influencer Content Engine</h1>
            </div>
            <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              {selectedInf ? `Monitoring ${selectedInf.name} · ${selectedInf.platform}` : 'Select an influencer or generate trend-based content'}
            </p>
          </div>
          {selectedInf && (
            <button onClick={() => checkLatest(selectedInf)} disabled={checkingChannel}
              data-testid="check-latest-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:scale-105 disabled:opacity-50"
              style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)', color: '#f97316' }}>
              {checkingChannel ? <><RefreshCw size={12} className="animate-spin" /> Checking...</> : <><Rss size={12} /> Check Latest</>}
            </button>
          )}
        </div>

        {/* Channel Check Results */}
        {channelData && (
          <div className="rounded-2xl p-4 space-y-3" style={{
            background: channelData.success && channelData.latest_videos?.length > 0 ? 'rgba(61,255,160,0.04)' : 'rgba(240,194,127,0.04)',
            border: `1px solid ${channelData.success && channelData.latest_videos?.length > 0 ? 'rgba(61,255,160,0.15)' : 'rgba(240,194,127,0.2)'}`,
          }}>
            {channelData.manual_paste_required ? (
              <div className="flex items-start gap-2 text-xs" style={{ color: '#f0c27f' }}>
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">{channelData.platform} — Manual Paste Required</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)' }}>{channelData.message}</div>
                </div>
              </div>
            ) : channelData.action_required === 'add_channel_id' ? (
              <div className="flex items-start gap-2 text-xs" style={{ color: '#60a5fa' }}>
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold mb-1">Add YouTube Channel ID</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)' }}>{channelData.message}</div>
                </div>
              </div>
            ) : channelData.latest_videos?.length > 0 ? (
              <div>
                <div className="flex items-center gap-2 mb-3 text-xs font-semibold" style={{ color: '#3dffa0' }}>
                  <PlayCircle size={13} /> {channelData.count} latest videos from {channelData.influencer}
                </div>
                <div className="space-y-2">
                  {channelData.latest_videos.map((v, i) => (
                    <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-white truncate">{v.title}</div>
                          <div className="text-[10px] mt-0.5 flex items-center gap-2" style={{ color: 'var(--llmc-text-muted)' }}>
                            <Clock size={9} /> {new Date(v.published).toLocaleDateString()}
                            {v.url && <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-blue-400 transition-colors" onClick={e => e.stopPropagation()}>
                              <ExternalLink size={9} /> View
                            </a>}
                          </div>
                          {v.description && (
                            <button className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.35)' }}
                              onClick={() => setExpandedVideo(expandedVideo === i ? null : i)}>
                              {expandedVideo === i ? <ChevronUp size={9} className="inline mr-1" /> : <ChevronDown size={9} className="inline mr-1" />}
                              {expandedVideo === i ? 'Hide' : 'Show'} description
                            </button>
                          )}
                          {expandedVideo === i && v.description && (
                            <div className="text-[10px] mt-1.5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)', whiteSpace: 'pre-wrap' }}>
                              {v.description}
                            </div>
                          )}
                        </div>
                        <button onClick={() => loadVideoForTranscript(v)}
                          className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all hover:opacity-80"
                          style={{ background: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.25)' }}>
                          <Repeat2 size={9} /> Use
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {channelData.message || 'No recent videos found.'}
              </div>
            )}
          </div>
        )}

        {/* Mode selector */}
        <div className="flex gap-2">
          {[
            { id: 'ai_decide', label: 'AI Decides', icon: Brain, color: '#a68eff' },
            { id: 'repurpose', label: 'Repurpose', icon: Repeat2, color: '#3dffa0' },
            { id: 'trend', label: 'Trend-Based', icon: TrendingUp, color: '#f97316' },
          ].map(m => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
              style={{
                background: mode === m.id ? `${m.color}20` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${mode === m.id ? `${m.color}60` : 'var(--llmc-border)'}`,
                color: mode === m.id ? m.color : 'var(--llmc-text-muted)',
              }}>
              <m.icon size={11} /> {m.label}
            </button>
          ))}
        </div>

        {/* Target config */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Target Platform</label>
            <select value={targetPlatform} onChange={e => setTargetPlatform(e.target.value)} className="llmc-input w-full px-3 py-2 text-xs">
              {TARGET_PLATFORMS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Content Type</label>
            <select value={contentType} onChange={e => setContentType(e.target.value)} className="llmc-input w-full px-3 py-2 text-xs">
              {CONTENT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Transcript input */}
        {mode !== 'trend' && (
          <div>
            <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>
              Paste Transcript / Content
            </label>
            <textarea value={transcript} onChange={e => setTranscript(e.target.value)}
              data-testid="transcript-input"
              className="llmc-input w-full px-3 py-2.5 text-sm resize-none" rows={6}
              placeholder={selectedInf ? `Paste ${selectedInf.name}'s transcript or use "Check Latest" to fetch YouTube content...` : 'Paste transcript or video description here...'} />
            <div className="text-[10px] mt-1" style={{ color: 'var(--llmc-text-muted)' }}>
              {mode === 'ai_decide' ? 'AI will decide whether to repurpose this or use trending topics instead' : 'AI will repurpose this into brand-aligned content'}
            </div>
          </div>
        )}

        {/* Trend topic */}
        {mode === 'trend' && (
          <div>
            <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Trend Topic</label>
            <input value={trendTopic} onChange={e => setTrendTopic(e.target.value)} data-testid="trend-topic-input"
              className="llmc-input w-full px-3 py-2 text-sm" placeholder="e.g. AI replacing jobs, GPT-5, TikTok ban alternatives..." />
          </div>
        )}

        {/* Generate */}
        <button onClick={generate}
          disabled={generating || (mode !== 'trend' && !transcript.trim()) || (mode === 'trend' && !trendTopic.trim())}
          data-testid="generate-content-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
          style={{ background: 'linear-gradient(135deg, #f97316, #f97316bb)', color: '#fff' }}>
          {generating ? <><RefreshCw size={14} className="animate-spin" /> Generating...</> : <><Zap size={14} /> Generate Content</>}
        </button>

        {/* Result */}
        {result?.content && (
          <div className="rounded-2xl p-5 space-y-4" style={{ background: 'var(--llmc-surface)', border: '1px solid rgba(249,115,22,0.25)' }}>
            {result.ai_decision && (
              <div className="flex items-center gap-2 text-[11px] px-3 py-1.5 rounded-lg" style={{ background: 'rgba(249,115,22,0.08)', color: '#f97316' }}>
                <Brain size={11} />
                AI decided to <strong>{result.ai_decision}</strong> · {result.ai_reason}
              </div>
            )}
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Title</div>
              <div className="text-sm font-bold text-white">{result.content.title}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Content</div>
              <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(255,255,255,0.75)', maxHeight: 300, overflowY: 'auto' }}>
                {result.content.body}
              </div>
            </div>
            {result.content.caption && (
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--llmc-text-muted)' }}>Caption</div>
                <div className="text-xs italic" style={{ color: 'rgba(255,255,255,0.6)' }}>{result.content.caption}</div>
              </div>
            )}
            {result.content.hashtags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {result.content.hashtags.map((h, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(249,115,22,0.1)', color: '#f97316' }}>{h}</span>
                ))}
              </div>
            )}
            <button onClick={copyContent} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--llmc-text-muted)' }}>
              {copied ? <Check size={12} style={{ color: '#3dffa0' }} /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Copy All'}
            </button>
          </div>
        )}
      </div>
      </div>
      )} {/* ── end Influencer Hub Tab ── */}

      {/* ── Repurpose Content Tab ────────────────────────────────── */}
      {mainTab === 'repurpose' && (
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Input */}
          <div className="w-[420px] flex-shrink-0 flex flex-col overflow-hidden" style={{ borderRight: '1px solid var(--llmc-border)' }}>
            <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
              <div className="flex items-center gap-2">
                <Repeat2 size={15} style={{ color: '#3dffa0' }} />
                <h2 className="text-sm font-black text-white">1 Piece → 7 Platforms</h2>
              </div>
              <p className="text-[11px] mt-1" style={{ color: 'var(--llmc-text-muted)' }}>
                Paste any long-form content and AI generates native posts for every platform simultaneously.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Title (optional)</label>
                <input value={rpTitle} onChange={e => setRpTitle(e.target.value)} className="llmc-input w-full px-3 py-2 text-sm" placeholder="Article or video title..." />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Content Type</label>
                <div className="flex flex-wrap gap-1.5">
                  {['blog', 'video_transcript', 'newsletter', 'podcast', 'thread'].map(t => (
                    <button key={t} onClick={() => setRpType(t)}
                      className="px-3 py-1 rounded-xl text-xs font-semibold transition-all"
                      style={{
                        background: rpType === t ? 'rgba(61,255,160,0.15)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${rpType === t ? 'rgba(61,255,160,0.4)' : 'rgba(255,255,255,0.08)'}`,
                        color: rpType === t ? '#3dffa0' : 'var(--llmc-text-muted)',
                      }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Content</label>
                  <span className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>{rpContent.trim().split(/\s+/).filter(Boolean).length} words</span>
                </div>
                <textarea value={rpContent} onChange={e => setRpContent(e.target.value)}
                  data-testid="repurpose-content-input"
                  className="llmc-input w-full px-3 py-2.5 text-sm resize-none" rows={9}
                  placeholder="Paste your blog article, video transcript, newsletter, podcast notes..." />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Target Platforms ({rpPlatforms.length})</label>
                  <div className="flex gap-2">
                    <button onClick={() => setRpPlatforms(REPURPOSE_PLATFORMS.map(p => p.id))} className="text-[10px]" style={{ color: '#a68eff' }}>All</button>
                    <button onClick={() => setRpPlatforms([])} className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>None</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {REPURPOSE_PLATFORMS.map(p => (
                    <button key={p.id} onClick={() => setRpPlatforms(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className="px-2.5 py-2 rounded-xl text-xs font-medium transition-all text-left"
                      style={{
                        background: rpPlatforms.includes(p.id) ? `${p.color}15` : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${rpPlatforms.includes(p.id) ? `${p.color}50` : 'rgba(255,255,255,0.08)'}`,
                        color: rpPlatforms.includes(p.id) ? p.color : 'rgba(255,255,255,0.4)',
                      }}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={repurposeContent} disabled={rpLoading || rpContent.trim().length < 50 || rpPlatforms.length === 0}
                data-testid="repurpose-btn"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-black transition-all hover:scale-[1.02] disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #3dffa0, #00d4b8)', color: '#0a0a12' }}>
                {rpLoading ? <><RefreshCw size={14} className="animate-spin" /> Repurposing...</> : <><Repeat2 size={14} /> Repurpose → {rpPlatforms.length} Platforms</>}
              </button>
            </div>
          </div>

          {/* Right: Results */}
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {rpLoading && (
              <div className="flex flex-col items-center justify-center py-24 space-y-4">
                <div className="relative w-16 h-16">
                  <div className="absolute inset-0 rounded-2xl animate-ping" style={{ background: 'rgba(61,255,160,0.15)' }} />
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(61,255,160,0.15)' }}>
                    <Repeat2 size={28} style={{ color: '#3dffa0' }} />
                  </div>
                </div>
                <div className="text-sm font-bold text-white">Generating {rpPlatforms.length} platform versions...</div>
              </div>
            )}
            {!rpLoading && !rpResults && (
              <div className="flex flex-col items-center justify-center py-24 space-y-3 text-center">
                <Repeat2 size={36} style={{ color: 'rgba(61,255,160,0.3)' }} />
                <div className="text-sm font-bold text-white">Transform Content Once, Post Everywhere</div>
                <div className="text-xs max-w-xs" style={{ color: 'var(--llmc-text-muted)' }}>Paste any long-form content and get native, engagement-optimized posts for all 7 platforms.</div>
              </div>
            )}
            {!rpLoading && rpResults && rpPlatforms.map(pid => {
              const p = REPURPOSE_PLATFORMS.find(x => x.id === pid);
              const result = rpResults[pid];
              if (!p || !result) return null;
              const mainContent = result?.tweet || result?.post || result?.caption || result?.script || result?.description || result?.content || '';
              return (
                <div key={pid} className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${p.color}30`, background: 'var(--llmc-surface)' }}>
                  <button onClick={() => setRpExpanded(e => ({ ...e, [pid]: !e[pid] }))}
                    className="w-full flex items-center justify-between p-4 text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[9px] font-black" style={{ background: `${p.color}20`, color: p.color, border: `1px solid ${p.color}30` }}>
                        {p.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="text-sm font-bold text-white">{p.name}</div>
                      {result?.hook && <div className="text-[11px] italic truncate max-w-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>"{result.hook.slice(0, 50)}..."</div>}
                    </div>
                    {rpExpanded[pid] ? <ChevronUp size={13} style={{ color: 'var(--llmc-text-muted)' }} /> : <ChevronDown size={13} style={{ color: 'var(--llmc-text-muted)' }} />}
                  </button>
                  {rpExpanded[pid] && (
                    <div className="px-4 pb-4 space-y-3">
                      {result?.title && <div className="text-xs font-semibold text-white">{result.title}</div>}
                      <div className="text-xs leading-relaxed whitespace-pre-wrap p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.8)', maxHeight: 250, overflowY: 'auto' }}>{mainContent}</div>
                      {result?.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {result.hashtags.map((h, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${p.color}15`, color: p.color }}>{h}</span>)}
                        </div>
                      )}
                      <button onClick={() => { const txt = [result.title, mainContent, (result.hashtags || []).join(' ')].filter(Boolean).join('\n\n'); navigator.clipboard.writeText(txt); }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
                        <Copy size={10} /> Copy
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── LinkedIn Trends Tab ────────────────────────────────────── */}
      {mainTab === 'linkedin' && (
        <div className="flex-1 overflow-y-auto p-5">
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(10,102,194,0.15)', border: '1px solid rgba(10,102,194,0.3)' }}>
              <Link2 size={18} style={{ color: '#0A66C2' }} />
            </div>
            <div>
              <h2 className="text-sm font-black text-white">LinkedIn Trending Posts</h2>
              <p className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>AI-curated viral post ideas for your niche — 1-click repurpose in your brand voice</p>
            </div>
          </div>

          {/* Niche selector + Generate */}
          <div className="flex items-center gap-3 mb-5">
            <select value={liNiche} onChange={e => setLiNiche(e.target.value)}
              className="llmc-input flex-1 max-w-xs px-3 py-2 text-xs" data-testid="linkedin-niche-select">
              {['AI & Technology', 'AI & Cybersecurity', 'B2B SaaS', 'Leadership', 'Marketing & Growth',
                'Future of Work', 'Entrepreneurship', 'Data Science', 'Product Management', 'Cloud & DevOps',
                'Blockchain & Web3', 'Sales & Revenue', 'Startups & VC'].map(n => (
                <option key={n}>{n}</option>
              ))}
            </select>
            <button onClick={fetchLinkedInTrends} disabled={liLoading}
              data-testid="fetch-linkedin-trends-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(10,102,194,0.2)', border: '1px solid rgba(10,102,194,0.4)', color: '#4da3ff' }}>
              {liLoading ? <RefreshCw size={13} className="animate-spin" /> : <TrendingUp size={13} />}
              {liLoading ? 'Analyzing Trends...' : 'Find Trending Posts'}
            </button>
          </div>

          {/* Trends grid */}
          {liTrends.length === 0 && !liLoading && (
            <div className="text-center py-12">
              <TrendingUp size={32} className="mx-auto mb-3 opacity-20" style={{ color: '#0A66C2' }} />
              <div className="text-sm font-bold text-white mb-1">No trends loaded yet</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Select a niche and click "Find Trending Posts" to see AI-curated LinkedIn content ideas</div>
            </div>
          )}

          <div className="space-y-4">
            {liTrends.map((post, i) => (
              <div key={post.id || i} className="rounded-2xl overflow-hidden"
                style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(10,102,194,0.15)', color: '#4da3ff', border: '1px solid rgba(10,102,194,0.25)' }}>
                          {post.format || 'insight'}
                        </span>
                        {post.engagement_prediction === 'high' && (
                          <span className="text-[10px] font-black px-2 py-0.5 rounded-full"
                            style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.2)' }}>
                            High Engagement
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-bold text-white mb-1 leading-snug">{post.hook}</div>
                      <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{post.body}</div>
                    </div>
                  </div>

                  {/* Hashtags */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(post.hashtags || []).slice(0, 5).map((h, j) => (
                      <span key={j} className="text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{ background: 'rgba(10,102,194,0.1)', color: '#4da3ff' }}>{h}</span>
                    ))}
                  </div>

                  {post.why_trending && (
                    <div className="text-[10px] mb-3 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.4)' }}>
                      Why it's trending: {post.why_trending}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button onClick={() => repurposeLinkedIn(post)} disabled={liRepurposing === post.id}
                      data-testid={`repurpose-linkedin-${i}`}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                      style={{ background: 'linear-gradient(135deg, rgba(10,102,194,0.2), rgba(77,163,255,0.15))', border: '1px solid rgba(10,102,194,0.3)', color: '#4da3ff' }}>
                      {liRepurposing === post.id ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />}
                      {liRepurposing === post.id ? 'Writing...' : 'Repurpose in My Voice'}
                    </button>
                    {liRepurposed[post.id] && (
                      <button onClick={() => {
                        navigator.clipboard.writeText(liRepurposed[post.id].full_post || '');
                        setLiCopied(post.id);
                        setTimeout(() => setLiCopied(null), 2000);
                      }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold hover:opacity-80"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
                        {liCopied === post.id ? <><Check size={11} style={{ color: '#3dffa0' }} /> Copied</> : <><Copy size={11} /> Copy</>}
                      </button>
                    )}
                  </div>
                </div>

                {/* Repurposed output */}
                {liRepurposed[post.id] && (
                  <div className="px-4 pb-4">
                    <div className="rounded-xl p-4"
                      style={{ background: 'rgba(10,102,194,0.06)', border: '1px solid rgba(10,102,194,0.2)' }}>
                      <div className="text-[10px] font-bold uppercase mb-2" style={{ color: '#4da3ff' }}>Your Brand Voice Version</div>
                      <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: '#e8e8f0' }}>
                        {liRepurposed[post.id].full_post || ''}
                      </div>
                      {liRepurposed[post.id].best_time_to_post && (
                        <div className="mt-2 text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                          Best time: {liRepurposed[post.id].best_time_to_post}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
